import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import { useAppDispatch } from 'src/hooks/useAppDispatch';
import { useAppSelector } from 'src/hooks/useAppSelector';
import {
    handleInputChange,
    resetNotificationState,
    pushToast,
} from 'src/features/notifications/notificationsSlice';
import {
    fetchEmailSettings,
    updateEmailSettings,
} from 'src/features/notifications/thunks';

// Comprehensive email validation with specific error messages
const validateEmailString = (
    emailString: string,
): { isValid: boolean; error: string } => {
    if (!emailString.trim()) {
        return { isValid: false, error: 'At least one email is required' };
    }

    // Check for semicolon separator (common mistake)
    if (/;/.test(emailString)) {
        return {
            isValid: false,
            error: 'Please separate email addresses with commas',
        };
    }

    // Split by comma and trim
    const emails = emailString
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email);

    // Check if any segment has multiple @ symbols (indicating missing comma separator)
    // or contains whitespace (e.g., "a@b.com c@d.com")
    const hasInvalidWhitespaceSeparator = emails.some((email) => {
        const atCount = (email.match(/@/g) || []).length;
        if (atCount > 1) {
            return true; // Multiple @ in one segment = missing comma
        }
        // Check for space or newline within what should be a single email
        if (/\s/.test(email) && /@/.test(email)) {
            return true;
        }
        return false;
    });

    if (hasInvalidWhitespaceSeparator) {
        return {
            isValid: false,
            error: 'Please separate email addresses with commas (max 30)',
        };
    }

    if (emails.length > 30) {
        return { isValid: false, error: 'Maximum 30 emails allowed' };
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const invalidEmails = emails.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
        return {
            isValid: false,
            error: 'Please enter valid email addresses',
        };
    }

    const duplicates = emails.filter(
        (email, idx) => emails.indexOf(email) !== idx,
    );
    if (duplicates.length > 0) {
        return {
            isValid: false,
            error: 'Duplicate email addresses are not allowed',
        };
    }

    return { isValid: true, error: '' };
};

export function useEmailSettingsLogic(): Record<string, any> {
    const { api } = useAppToolchain();
    const { auth } = useAuth();
    const dispatch = useAppDispatch();
    const [validEmail, setValidEmail] = useState(true);
    const [emailError, setEmailError] = useState('');

    // Get email settings from Redux
    const formState = useAppSelector((_state) => _state.notifications);
    const {
        sendDigests,
        sendEndUserAlerts,
        showSenders,
        emailDestinations,
        sendRemediationAlerts,
        alertsBatchingHourlyInterval,
        loading,
    } = formState;

    const validateEmails = useMemo(() => {
        if (!showSenders || !sendDigests) return true;
        const result = validateEmailString(emailDestinations);
        return result.isValid;
    }, [emailDestinations, showSenders, sendDigests]);

    const handleEmailBlur = useCallback(() => {
        if (!showSenders || !sendDigests) {
            setValidEmail(true);
            setEmailError('');
            return;
        }
        const result = validateEmailString(emailDestinations);
        setValidEmail(result.isValid);
        setEmailError(result.error);
    }, [emailDestinations, showSenders, sendDigests]);

    const handleSaveClick = useCallback(() => {
        if (loading) return;
        if (!validateEmails) {
            const result = validateEmailString(emailDestinations);
            setValidEmail(false);
            setEmailError(result.error);
            dispatch(
                pushToast({
                    type: 'error',
                    message: result.error || 'Please enter valid email addresses',
                }),
            );
            return;
        }
        setValidEmail(true);
        dispatch(
            updateEmailSettings({
                settings: formState,
                api,
                id: auth?.accountDetails?.account?.id,
            }),
        )
            .unwrap()
            .then(() => {
                dispatch(
                    pushToast({
                        type: 'success',
                        message: 'Email settings saved successfully!',
                    }),
                );
            })
            .catch(() => {
                dispatch(
                    pushToast({
                        type: 'error',
                        message: 'Failed to save email settings. Please try again.',
                    }),
                );
            });
    }, [dispatch, formState, validateEmails, loading, api, auth?.accountDetails?.account?.id]);

    const handleFieldChange = useCallback(
        (name: keyof typeof formState, value: any) => {
            dispatch(handleInputChange({ name, value }));
        },
        [dispatch],
    );

    // Effects should always come last
    useEffect(() => {
        dispatch(
            fetchEmailSettings({
                api,
                id: auth?.accountDetails?.account?.id,
            }),
        );
    }, [dispatch, api, auth?.accountDetails?.account?.id]);

    useEffect(() => {
        return () => {
            dispatch(resetNotificationState());
        };
    }, [dispatch]);

    return {
        formState,
        validEmail,
        emailError,
        validateEmails,
        handleSaveClick,
        handleFieldChange,
        handleEmailBlur,
        loading,
        sendDigests,
        sendEndUserAlerts,
        showSenders,
        emailDestinations,
        sendRemediationAlerts,
        alertsBatchingHourlyInterval,
    };
}
